import circleBg from '@/assets/notifications/limit-order-expired/medallion-circle-bg.svg';
import hourglass from '@/assets/notifications/limit-order-expired/medallion-hourglass.png';
import { POPUP_CTA } from '@/lib/notifications/popup-cta';
import { marketTitle, metaNumber, priceCentsLabel } from '@/lib/notifications/meta';
import { Medallion } from '../Medallion';
import { PopupCta } from '../PopupCta';
import type { PopupViewProps } from '../types';

const CTA = POPUP_CTA['limit-order-expired'];

/**
 * LIMIT_ORDER_EXPIRED — neutral/purple tone (informational expiry, not a loss):
 * purple hourglass medallion, "Order Expired", the market title, an expiry
 * sentence, and a "No contracts were bought." footer. CTA "Got it" → dismiss.
 *
 * GAP: `limitOrderExpiredNotification` (predictions-api) only stores `orderId` +
 * market fields (`marketName`) in `meta` — the qty/price the Figma sentence wants
 * live in the pre-rendered copy vars, NOT in `meta`. So we render the detailed
 * "{qty} contracts @ {price}¢" sentence when those keys ARE present (reading
 * `orderQuantity`/`price`, the natural keys were the producer extended), and
 * gracefully drop to a generic sentence otherwise (the current production path).
 */
export function LimitOrderExpiredPopup({ notification, onAction }: PopupViewProps) {
  const meta = notification.meta;
  const title = marketTitle(meta);
  const qty = metaNumber(meta, 'orderQuantity') ?? metaNumber(meta, 'quantity');
  const price = metaNumber(meta, 'price') ?? metaNumber(meta, 'avgPrice');
  const hasDetail = qty !== undefined || price !== undefined;

  return (
    <>
      <Medallion circleSrc={circleBg} illustrationSrc={hourglass} tone="neutral" alt="" />

      <div className="flex flex-col items-center gap-4">
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-3xl font-semibold tracking-[-0.6px] text-ink">Order Expired</h2>
          {title && <p className="text-xl font-bold text-ink">{`“${title}”`}</p>}
        </div>

        <p className="text-xl text-ink">
          {hasDetail ? (
            <>
              Your limit order for{' '}
              <span className="font-bold">
                {qty !== undefined ? `${qty} contracts` : 'your contracts'}
                {price !== undefined ? ` @ ${priceCentsLabel(price)}` : ''}
              </span>{' '}
              expired without being executed.
            </>
          ) : (
            'Your limit order expired without being executed.'
          )}
        </p>

        <p className="text-xl text-ink">No contracts were bought.</p>
      </div>

      <PopupCta
        label={CTA.primary.label}
        width={234}
        onClick={() => onAction(CTA.primary.action)}
        testId="popup-cta-primary"
      />
    </>
  );
}
